import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Shield,
  Search,
  Filter,
  ChevronLeft,
  ChevronRight,
  X,
  Clock,
  User,
  Globe,
  Monitor,
  FileText,
  LogIn,
  LogOut,
  KeyRound,
  Pencil,
  Trash2,
  UserPlus,
  Banknote,
  RefreshCw,
  AlertTriangle,
  Eye,
  Scale,
} from 'lucide-react';
import { auditLogsApi, type AuditLogRecord, type AuditLogStats } from '../api';
import { ExportMenu, type ExportRow } from '../lib/exportReports';
import DateRangeFilter from '../components/common/DateRangeFilter';
import { useDateRange, formatRangeLabel } from '../lib/dateFilter';
import { formatLocalYmd } from '../lib/loanScheduleDates';
import { useTranslation } from '../lib/i18n';
import { isAuditorUser } from '../lib/permissions';

const ACTION_META: Record<
  string,
  { label: string; tone: string; icon: React.ComponentType<{ className?: string }> }
> = {
  login: { label: 'Login', tone: 'bg-emerald-50 text-emerald-700 ring-emerald-600/20', icon: LogIn },
  logout: { label: 'Logout', tone: 'bg-zinc-100 text-zinc-700 ring-zinc-500/20', icon: LogOut },
  password_change: { label: 'Password', tone: 'bg-amber-50 text-amber-800 ring-amber-600/20', icon: KeyRound },
  customer_created: { label: 'Customer', tone: 'bg-sky-50 text-sky-700 ring-sky-600/20', icon: UserPlus },
  loan_created: { label: 'Loan created', tone: 'bg-emerald-50 text-emerald-700 ring-emerald-600/20', icon: FileText },
  loan_updated: { label: 'Loan updated', tone: 'bg-amber-50 text-amber-800 ring-amber-600/20', icon: Pencil },
  loan_deleted: { label: 'Loan deleted', tone: 'bg-rose-50 text-rose-700 ring-rose-600/20', icon: Trash2 },
  emi_collected: { label: 'EMI collected', tone: 'bg-emerald-50 text-emerald-700 ring-emerald-600/20', icon: Banknote },
  overdue_waived: { label: 'Overdue waived', tone: 'bg-violet-50 text-violet-700 ring-violet-600/20', icon: Scale },
  emi_deleted: { label: 'EMI removed', tone: 'bg-rose-50 text-rose-700 ring-rose-600/20', icon: Trash2 },
  emi_updated: { label: 'EMI corrected', tone: 'bg-amber-50 text-amber-800 ring-amber-600/20', icon: Pencil },
  admin_created: { label: 'Admin added', tone: 'bg-violet-50 text-violet-700 ring-violet-600/20', icon: UserPlus },
  admin_updated: { label: 'Admin updated', tone: 'bg-amber-50 text-amber-800 ring-amber-600/20', icon: Pencil },
  admin_deleted: { label: 'Admin removed', tone: 'bg-rose-50 text-rose-700 ring-rose-600/20', icon: Trash2 },
  role_changed: { label: 'Role changed', tone: 'bg-violet-50 text-violet-700 ring-violet-600/20', icon: User },
  reverse_entry: { label: 'Reversed', tone: 'bg-orange-50 text-orange-800 ring-orange-600/20', icon: RefreshCw },
};

const ACTION_FILTERS = [
  { id: '', label: 'All actions' },
  { id: 'login,logout', label: 'Sign in / out' },
  { id: 'loan_created,loan_updated,loan_deleted', label: 'Loans' },
  { id: 'emi_collected,emi_deleted', label: 'Collections' },
  { id: 'admin_created,admin_updated,admin_deleted,role_changed', label: 'Admins & roles' },
  { id: 'password_change', label: 'Security' },
  { id: 'customer_created', label: 'Customers' },
];

function fmtTime(iso?: string) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function fmtTimeShort(iso?: string) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
}

function fmtDateShort(iso?: string) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
}

function actionBadge(action: string) {
  const meta = ACTION_META[action] || {
    label: action.replace(/_/g, ' '),
    tone: 'bg-zinc-100 text-zinc-700 ring-zinc-500/20',
    icon: FileText,
  };
  const Icon = meta.icon;
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold ring-1 ring-inset ${meta.tone}`}
    >
      <Icon className="h-3 w-3 shrink-0" />
      <span className="truncate">{meta.label}</span>
    </span>
  );
}

function formatChangeValue(val: unknown): string {
  if (val == null) return '—';
  if (typeof val === 'object') return JSON.stringify(val);
  return String(val);
}

function StatCard({
  label,
  value,
  sub,
  tone = 'zinc',
}: {
  label: string;
  value: number | string;
  sub?: string;
  tone?: 'zinc' | 'emerald' | 'amber' | 'rose' | 'violet';
}) {
  const tones = {
    zinc: 'border-zinc-200 bg-white dark:border-zinc-700 dark:bg-zinc-900',
    emerald: 'border-emerald-200/80 bg-emerald-50/50 dark:border-emerald-800/50 dark:bg-emerald-950/30',
    amber: 'border-amber-200/80 bg-amber-50/50 dark:border-amber-800/50 dark:bg-amber-950/30',
    rose: 'border-rose-200/80 bg-rose-50/50 dark:border-rose-800/50 dark:bg-rose-950/30',
    violet: 'border-violet-200/80 bg-violet-50/50 dark:border-violet-800/50 dark:bg-violet-950/30',
  };
  return (
    <div className={`rounded-xl border p-3 sm:p-4 ${tones[tone]}`}>
      <p className="text-[11px] font-semibold uppercase tracking-wide text-zinc-500 dark:text-zinc-400">{label}</p>
      <p className="mt-1 text-2xl font-bold tabular-nums text-zinc-900 dark:text-zinc-100">{value}</p>
      {sub ? <p className="mt-0.5 text-xs text-zinc-500 dark:text-zinc-400">{sub}</p> : null}
    </div>
  );
}

function LogDetailDrawer({
  log,
  onClose,
}: {
  log: AuditLogRecord | null;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  if (!log) return null;

  return (
    <div className="fixed inset-0 z-[70] flex justify-end">
      <button type="button" className="absolute inset-0 bg-black/40" onClick={onClose} aria-label="Close" />
      <div className="relative flex h-full w-full max-w-lg flex-col bg-white shadow-2xl dark:bg-zinc-900 sm:max-w-md">
        <div className="flex items-start justify-between border-b border-zinc-200 px-4 py-4 dark:border-zinc-700">
          <div className="min-w-0 pr-3">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-emerald-600 dark:text-emerald-400">
              {t('audit.detailTitle')}
            </p>
            <h3 className="mt-0.5 truncate text-base font-semibold text-zinc-900 dark:text-zinc-100">{log.summary || log.action}</h3>
            <p className="mt-1 text-xs text-zinc-500 dark:text-zinc-400">{fmtTime(log.createdAt)}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-2 text-zinc-500 hover:bg-zinc-100 dark:text-zinc-400 dark:hover:bg-zinc-800"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-4 py-4">
          <div className="mb-4 flex flex-wrap gap-2">{actionBadge(log.action)}</div>

          <dl className="space-y-3 text-sm">
            <div className="rounded-lg border border-zinc-200 bg-zinc-50/80 p-3 dark:border-zinc-700 dark:bg-zinc-800/90">
              <dt className="text-[11px] font-semibold uppercase tracking-wide text-zinc-500 dark:text-zinc-400">{t('audit.actor')}</dt>
              <dd className="mt-1 font-medium text-zinc-900 dark:text-zinc-100">{log.actorName || '—'}</dd>
              <dd className="text-xs text-zinc-500 dark:text-zinc-400">{log.actorEmail}</dd>
              {log.actorDeskRole ? (
                <dd className="mt-1 text-xs capitalize text-zinc-600 dark:text-zinc-300">{log.actorDeskRole.replace(/_/g, ' ')}</dd>
              ) : null}
            </div>

            {(log.entityLabel || log.entityId) && (
              <div className="rounded-lg border border-zinc-200 p-3 dark:border-zinc-700 dark:bg-zinc-900/50">
                <dt className="text-[11px] font-semibold uppercase tracking-wide text-zinc-500 dark:text-zinc-400">{t('audit.entity')}</dt>
                <dd className="mt-1 font-medium text-zinc-900 dark:text-zinc-100">{log.entityLabel || log.entityId}</dd>
                {log.entityType ? <dd className="text-xs capitalize text-zinc-500">{log.entityType}</dd> : null}
              </div>
            )}

            {log.reason ? (
              <div className="rounded-lg border border-amber-200 bg-amber-50/60 p-3">
                <dt className="text-[11px] font-semibold uppercase tracking-wide text-amber-800">{t('audit.reason')}</dt>
                <dd className="mt-1 text-sm text-amber-900">{log.reason}</dd>
              </div>
            ) : null}

            <div className="grid grid-cols-2 gap-2">
              <div className="rounded-lg border border-zinc-200 p-3">
                <dt className="flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wide text-zinc-500">
                  <Globe className="h-3 w-3" /> IP
                </dt>
                <dd className="mt-1 font-mono text-xs text-zinc-800">{log.ipAddress || '—'}</dd>
              </div>
              <div className="rounded-lg border border-zinc-200 p-3">
                <dt className="flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wide text-zinc-500">
                  <Monitor className="h-3 w-3" /> {t('audit.device')}
                </dt>
                <dd className="mt-1 text-xs text-zinc-800">{log.device || '—'}</dd>
              </div>
            </div>
          </dl>

          {log.changes && log.changes.length > 0 ? (
            <div className="mt-5">
              <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-zinc-500">
                {t('audit.changes')}
              </h4>
              <div className="space-y-2">
                {log.changes.map((c, i) => (
                  <div key={`${c.field}-${i}`} className="rounded-lg border border-zinc-200 overflow-hidden">
                    <div className="bg-zinc-50 px-3 py-1.5 text-xs font-semibold text-zinc-700">{c.field}</div>
                    <div className="grid grid-cols-1 divide-y divide-zinc-100 sm:grid-cols-2 sm:divide-x sm:divide-y-0">
                      <div className="px-3 py-2">
                        <p className="text-[10px] font-semibold uppercase text-rose-600">{t('audit.oldValue')}</p>
                        <p className="mt-0.5 break-all text-xs text-zinc-700">{formatChangeValue(c.oldValue)}</p>
                      </div>
                      <div className="px-3 py-2">
                        <p className="text-[10px] font-semibold uppercase text-emerald-600">{t('audit.newValue')}</p>
                        <p className="mt-0.5 break-all text-xs text-zinc-700">{formatChangeValue(c.newValue)}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

export function AuditLogs({ setActiveItem: _setActiveItem }: { setActiveItem: (item: string) => void }) {
  const { t } = useTranslation();
  const dr = useDateRange('this_month');
  const [search, setSearch] = useState('');
  const [actionFilter, setActionFilter] = useState('');
  const [page, setPage] = useState(1);
  const [logs, setLogs] = useState<AuditLogRecord[]>([]);
  const [stats, setStats] = useState<AuditLogStats | null>(null);
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0, limit: 50 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selected, setSelected] = useState<AuditLogRecord | null>(null);
  const [showFilters, setShowFilters] = useState(false);

  const rangeLabel = formatRangeLabel(dr.range);
  const isAuditor = isAuditorUser();

  const fromIso = dr.range.start ? formatLocalYmd(dr.range.start) : undefined;
  const toIso = dr.range.end ? formatLocalYmd(dr.range.end) : undefined;

  const queryParams = useMemo(
    () => ({
      page,
      limit: 50,
      from: fromIso,
      to: toIso,
      action: actionFilter || undefined,
      search: search.trim() || undefined,
    }),
    [page, fromIso, toIso, actionFilter, search]
  );

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [listRes, statsRes] = await Promise.all([
        auditLogsApi.list(queryParams),
        auditLogsApi.stats({ from: fromIso, to: toIso }),
      ]);
      setLogs(listRes.logs);
      setPagination(listRes.pagination);
      setStats(statsRes);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load audit logs');
      setLogs([]);
    } finally {
      setLoading(false);
    }
  }, [queryParams, fromIso, toIso]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    setPage(1);
  }, [fromIso, toIso, actionFilter, search]);

  const exportRows: ExportRow[] = useMemo(
    () =>
      logs.map((log) => {
        const userLine = [log.actorName, log.actorEmail].filter(Boolean).join('\n');
        const origin = [log.ipAddress, log.device].filter(Boolean).join('\n');
        return {
          Time: fmtTime(log.createdAt),
          User: userLine,
          Action: log.action,
          Entity: log.entityLabel || log.entityId,
          Summary: log.summary,
          Reason: log.reason || '',
          Origin: origin,
        };
      }),
    [logs]
  );

  return (
    <div className="space-y-4 pb-6 sm:space-y-5">
      {/* Header */}
      <div className="app-card overflow-hidden">
        <div className="relative bg-gradient-to-br from-zinc-900 via-zinc-900 to-emerald-950 px-4 py-3 sm:px-5 sm:py-3.5">
          <div className="relative flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-emerald-500/20 text-emerald-300 ring-1 ring-emerald-400/30">
                  <Shield className="h-4 w-4" />
                </span>
                <div>
                  <h1 className="text-base font-bold text-white sm:text-lg">{t('audit.title')}</h1>
                  <p className="text-[11px] text-emerald-200/80 sm:text-xs">{t('audit.subtitle')}</p>
                </div>
              </div>
              {isAuditor ? (
                <div className="mt-2 inline-flex items-center gap-1.5 rounded-md bg-white/10 px-2 py-0.5 text-[11px] text-emerald-100 ring-1 ring-white/10">
                  <Eye className="h-3 w-3" />
                  {t('audit.auditorMode')}
                </div>
              ) : null}
            </div>
            <div className="shrink-0">
              <ExportMenu
                title={t('audit.exportTitle')}
                excelFilename={`audit-trail-${fromIso || 'all'}`}
                rows={exportRows}
                landscape
              />
            </div>
          </div>
        </div>

        <div className="border-t border-amber-200/80 bg-amber-50/80 px-4 py-1.5 sm:px-5 dark:border-amber-800/50 dark:bg-amber-950/35">
          <p className="flex items-center gap-1.5 text-[11px] leading-snug text-amber-900 dark:text-amber-100 sm:text-xs">
            <AlertTriangle className="h-3.5 w-3.5 shrink-0 text-amber-600" />
            {t('audit.disclaimer')}
          </p>
        </div>
      </div>

      {/* Stats */}
      {stats ? (
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-5 lg:gap-3">
          <StatCard label={t('audit.statTotal')} value={stats.total} sub={rangeLabel} />
          <StatCard label={t('audit.statToday')} value={stats.today} tone="emerald" />
          <StatCard label={t('audit.statLogins')} value={stats.logins} tone="violet" />
          <StatCard label={t('audit.statUpdates')} value={stats.updates} tone="amber" />
          <StatCard label={t('audit.statSecurity')} value={stats.security} tone="rose" />
        </div>
      ) : null}

      {/* Filters */}
      <div className="app-card p-3 sm:p-4">
        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            <div className="relative min-w-0 flex-1">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-400" />
              <input
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t('audit.searchPlaceholder')}
                className="w-full rounded-lg border border-zinc-200 bg-white py-2.5 pl-9 pr-3 text-sm text-zinc-900 placeholder:text-zinc-400 focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 dark:border-zinc-600 dark:bg-zinc-800 dark:text-zinc-100 dark:placeholder:text-zinc-500"
              />
            </div>
            <button
              type="button"
              onClick={() => setShowFilters((v) => !v)}
              className="inline-flex items-center justify-center gap-2 rounded-lg border border-zinc-200 bg-white px-3 py-2.5 text-sm font-medium text-zinc-700 hover:bg-zinc-50 dark:border-zinc-600 dark:bg-zinc-800 dark:text-zinc-200 dark:hover:bg-zinc-700 lg:hidden"
            >
              <Filter className="h-4 w-4" />
              {t('common.filter')}
            </button>
            <button
              type="button"
              onClick={() => load()}
              className="inline-flex items-center justify-center gap-2 rounded-lg border border-zinc-200 bg-white px-3 py-2.5 text-sm font-medium text-zinc-700 hover:bg-zinc-50 dark:border-zinc-600 dark:bg-zinc-800 dark:text-zinc-200 dark:hover:bg-zinc-700"
            >
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              {t('audit.refresh')}
            </button>
          </div>

          <div className={`flex flex-col gap-3 lg:flex-row lg:items-end ${showFilters ? '' : 'hidden lg:flex'}`}>
            <div className="min-w-0 flex-1">
              <DateRangeFilter
                start={dr.range.start}
                end={dr.range.end}
                preset={dr.preset}
                onStart={dr.setStart}
                onEnd={dr.setEnd}
                onPreset={dr.setPreset}
                onClear={dr.clear}
                className="!w-full"
              />
            </div>
            <div className="w-full lg:w-48">
              <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-zinc-500">
                {t('audit.actionFilter')}
              </label>
              <select
                value={actionFilter}
                onChange={(e) => setActionFilter(e.target.value)}
                className="w-full rounded-lg border border-zinc-200 bg-white px-3 py-2 text-sm text-zinc-900 focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 dark:border-zinc-600 dark:bg-zinc-800 dark:text-zinc-100"
              >
                {ACTION_FILTERS.map((f) => (
                  <option key={f.id || 'all'} value={f.id}>
                    {f.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>

      {error ? (
        <div className="app-card p-4 text-center text-sm text-rose-600">{error}</div>
      ) : null}

      {/* Table — desktop */}
      <div className="app-card hidden min-w-0 overflow-hidden lg:block">
        <div className="flex items-center justify-between border-b border-zinc-200 bg-zinc-50/80 px-4 py-3 dark:border-zinc-700 dark:bg-zinc-800/90">
          <div>
            <h2 className="text-sm font-semibold text-zinc-900 dark:text-zinc-100">{t('audit.tableTitle')}</h2>
            <p className="text-xs text-zinc-500 dark:text-zinc-400">
              {pagination.total.toLocaleString('en-IN')} {t('audit.events')} · {rangeLabel}
            </p>
          </div>
        </div>

        {loading ? (
          <div className="p-10 text-center text-sm text-zinc-500">{t('common.loading')}</div>
        ) : logs.length === 0 ? (
          <div className="p-10 text-center">
            <Shield className="mx-auto h-10 w-10 text-zinc-300" />
            <p className="mt-2 text-sm font-medium text-zinc-700">{t('audit.empty')}</p>
            <p className="mt-1 text-xs text-zinc-500">{t('audit.emptyHint')}</p>
          </div>
        ) : (
          <div className="app-table-wrap overflow-x-auto">
            <table className="app-table w-full min-w-[56rem]">
              <thead>
                <tr>
                  <th className="w-36">{t('common.date')}</th>
                  <th>{t('audit.actor')}</th>
                  <th>{t('audit.action')}</th>
                  <th>{t('audit.summary')}</th>
                  <th className="w-28">{t('audit.device')}</th>
                  <th className="w-28">IP</th>
                  <th className="w-16" />
                </tr>
              </thead>
              <tbody>
                {logs.map((log) => (
                  <tr
                    key={log.id}
                    className="cursor-pointer hover:bg-emerald-50/40 dark:hover:bg-emerald-950/25"
                    onClick={() => setSelected(log)}
                  >
                    <td className="whitespace-nowrap text-xs text-zinc-600">
                      <div className="flex items-center gap-1.5">
                        <Clock className="h-3.5 w-3.5 text-zinc-400" />
                        {fmtTime(log.createdAt)}
                      </div>
                    </td>
                    <td>
                      <div className="min-w-0">
                        <p className="truncate text-sm font-medium text-zinc-900">{log.actorName || '—'}</p>
                        <p className="truncate text-xs text-zinc-500">{log.actorEmail}</p>
                      </div>
                    </td>
                    <td>{actionBadge(log.action)}</td>
                    <td>
                      <p className="line-clamp-2 text-sm text-zinc-800">{log.summary}</p>
                      {log.entityLabel ? (
                        <p className="mt-0.5 truncate text-xs text-zinc-500">{log.entityLabel}</p>
                      ) : null}
                    </td>
                    <td className="text-xs text-zinc-600">{log.device || '—'}</td>
                    <td className="font-mono text-xs text-zinc-500">{log.ipAddress || '—'}</td>
                    <td>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelected(log);
                        }}
                        className="rounded-md px-2 py-1 text-xs font-semibold text-emerald-700 hover:bg-emerald-50"
                      >
                        {t('common.view')}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {pagination.pages > 1 ? (
          <div className="flex items-center justify-between border-t border-zinc-200 px-4 py-3">
            <p className="text-xs text-zinc-500">
              Page {pagination.page} of {pagination.pages}
            </p>
            <div className="flex gap-1">
              <button
                type="button"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                className="rounded-lg border border-zinc-200 p-2 disabled:opacity-40"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <button
                type="button"
                disabled={page >= pagination.pages}
                onClick={() => setPage((p) => p + 1)}
                className="rounded-lg border border-zinc-200 p-2 disabled:opacity-40"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        ) : null}
      </div>

      {/* Cards — mobile */}
      <div className="space-y-2 lg:hidden">
        <div className="flex items-center justify-between px-0.5">
          <p className="text-xs font-semibold text-zinc-600">
            {pagination.total.toLocaleString('en-IN')} {t('audit.events')}
          </p>
          <p className="text-xs text-zinc-500">{rangeLabel}</p>
        </div>

        {loading ? (
          <div className="app-card p-8 text-center text-sm text-zinc-500">{t('common.loading')}</div>
        ) : logs.length === 0 ? (
          <div className="app-card p-8 text-center">
            <Shield className="mx-auto h-10 w-10 text-zinc-300" />
            <p className="mt-2 text-sm font-medium text-zinc-700">{t('audit.empty')}</p>
          </div>
        ) : (
          logs.map((log) => (
            <button
              key={log.id}
              type="button"
              onClick={() => setSelected(log)}
              className="app-card w-full p-3 text-left transition-colors active:bg-zinc-50"
            >
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    {actionBadge(log.action)}
                    <span className="text-[11px] text-zinc-500">
                      {fmtDateShort(log.createdAt)} · {fmtTimeShort(log.createdAt)}
                    </span>
                  </div>
                  <p className="mt-1.5 line-clamp-2 text-sm font-medium text-zinc-900">{log.summary}</p>
                  <p className="mt-1 truncate text-xs text-zinc-500">
                    {log.actorName} · {log.device || 'Unknown device'}
                  </p>
                </div>
                <ChevronRight className="mt-1 h-4 w-4 shrink-0 text-zinc-400" />
              </div>
            </button>
          ))
        )}

        {pagination.pages > 1 ? (
          <div className="flex items-center justify-center gap-3 pt-2">
            <button
              type="button"
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              className="app-btn-secondary text-sm disabled:opacity-40"
            >
              {t('audit.prev')}
            </button>
            <span className="text-xs text-zinc-500">
              {pagination.page} / {pagination.pages}
            </span>
            <button
              type="button"
              disabled={page >= pagination.pages}
              onClick={() => setPage((p) => p + 1)}
              className="app-btn-secondary text-sm disabled:opacity-40"
            >
              {t('audit.next')}
            </button>
          </div>
        ) : null}
      </div>

      <LogDetailDrawer log={selected} onClose={() => setSelected(null)} />
    </div>
  );
}
